import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { peopleApi } from "@/lib/api/people";
import { PROTECTED_ROUTES, legacyFacultyDestination } from "@/navigation/routes";

/** The People reading of every faculty member, the safe way on from a lost bookmark. */
const FACULTY_READING = `${PROTECTED_ROUTES.admin.people}?relationship=faculty`;

/**
 * A retired Faculty directory record bookmark (`?faculty={profileId}`). A
 * faculty profile id names no person, so the server resolves it to a person key
 * first; the arrival then replaces itself with that person's teaching view,
 * keeping every other parameter and the hash (`legacyFacultyDestination`).
 * `RegistrarRecordEntry` mounts this for that one arrival; every other retired
 * People address resolves without a lookup.
 */
export function LegacyFacultyLookup() {
  const { search, hash } = useLocation();
  const navigate = useNavigate();
  const facultyId = new URLSearchParams(search).get("faculty");
  const facultyKey = useQuery({
    queryKey: ["people", "legacy-faculty", facultyId],
    queryFn: () => peopleApi.resolveLegacyFaculty(facultyId as string),
    enabled: !!facultyId,
    retry: false,
  });

  useEffect(() => {
    if (!facultyKey.data) return;
    navigate(legacyFacultyDestination(facultyKey.data.key, search, hash), { replace: true });
  }, [facultyKey.data, hash, navigate, search]);

  if (!facultyId) return <Navigate to={FACULTY_READING} replace />;

  return (
    <div className="flex min-h-[45vh] items-center justify-center px-4">
      {facultyKey.isPending && (
        <section role="status" aria-live="polite" className="max-w-md text-center">
          <h2 className="text-title font-semibold">Opening this faculty record</h2>
          <p className="mt-2 text-ui text-muted-foreground">
            Resolving the retired Faculty bookmark into People.
          </p>
        </section>
      )}
      {facultyKey.isError && (
        <section aria-labelledby="legacy-faculty-error" className="max-w-md text-center">
          <h2 id="legacy-faculty-error" className="text-title font-semibold">
            This faculty bookmark could not be resolved
          </h2>
          <p className="mt-2 text-ui text-muted-foreground">
            The profile may no longer exist or may not belong to this tenant. Nothing was changed.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button type="button" variant="outline" onClick={() => void facultyKey.refetch()}>
              Try again
            </Button>
            <Button asChild>
              <Link to={FACULTY_READING} replace>Continue to People</Link>
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
